/**
 * AdminTemplatesPage — Template library management.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, FileText, Plus, Search, X, ChevronDown, CircleDot } from 'lucide-react';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';
import type { TemplateStatus } from '../../../types/adminConsole';

const statusBadge: Record<TemplateStatus, string> = {
  draft: 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400',
  published: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  standard: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  archived: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-900/40 dark:text-zinc-500',
};

const statusLabel: Record<TemplateStatus, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  standard: 'Padrão',
  archived: 'Arquivado',
};

const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  value_stream_map: 'Mapa de Fluxo de Valor',
  kaizen_event: 'Evento Kaizen',
  action_plan: 'Plano de Ação',
  a3_report: 'Relatório A3',
  audit_checklist: 'Checklist de Auditoria',
  workflow: 'Workflow',
};

export const AdminTemplatesPage: React.FC = () => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const filtersWidget = useMemo(() => (
    <div className="flex flex-1 items-center justify-end gap-1.5">
      <div className="relative">
        <select
          className={`${filterBtnClass} appearance-none cursor-pointer`}
          style={{ paddingLeft: 22, paddingRight: 22 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos</option>
          <option value="published">Publicado</option>
          <option value="standard">Padrão</option>
          <option value="draft">Rascunho</option>
          <option value="archived">Arquivado</option>
        </select>
        <CircleDot size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
        <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
      </div>
      <div className="flex w-[220px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar template..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {search && (
            <button onClick={() => setSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  ), [statusFilter, search]);

  const templatesQuery = useQuery({
    queryKey: ['admin-templates', orgId],
    enabled: Boolean(orgId),
    queryFn: () => AdminConsoleService.getTemplates(orgId),
  });

  const templates = templatesQuery.data ?? [];
  const filtered = templates.filter((t) => {
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase()) || (t.category ?? '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout ribbonSlots={{ afterGroups: filtersWidget }}>
        <div className="enterprise-page h-full overflow-y-auto">

          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--block-gap)]">
            {(['all', 'published', 'standard', 'draft'] as const).map((s) => {
              const count = s === 'all' ? templates.length : templates.filter((t) => t.status === s).length;
              const label = s === 'all' ? 'Total' : statusLabel[s];
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`enterprise-card-surface p-[var(--card-padding)] text-left transition-colors ${statusFilter === s ? 'ring-1 ring-enterprise-primary' : 'hover:bg-enterprise-panel'}`}
                >
                  <p className="enterprise-field-label">{label}</p>
                  <p className="mt-1 text-xl font-semibold enterprise-text-primary tabular-nums">{count}</p>
                </button>
              );
            })}
          </div>

          {templatesQuery.isPending && <ModuleLoadingState label="Carregando templates" />}

          {!templatesQuery.isPending && (
            <ModuleCard
              title={`Templates (${filtered.length})`}
            >
              {!templates.length ? (
                <div className="py-8 text-center">
                  <FileText size={24} className="mx-auto enterprise-text-secondary opacity-40 mb-2" />
                  <p className="text-[13px] enterprise-text-secondary">Nenhum template encontrado.</p>
                  <p className="text-[11px] enterprise-text-secondary mt-1">
                    Templates são criados na seção Padrões (Excelência).
                  </p>
                </div>
              ) : !filtered.length ? (
                <p className="text-[12px] enterprise-text-secondary py-4 text-center">Nenhum resultado para os filtros.</p>
              ) : (
                <div className="enterprise-table-shell">
                  <table className="w-full text-left text-sm">
                    <thead className="enterprise-table-header">
                      <tr>
                        <th className="enterprise-table-head-cell">Template</th>
                        <th className="enterprise-table-head-cell">Categoria</th>
                        <th className="enterprise-table-head-cell">Tipo</th>
                        <th className="enterprise-table-head-cell">Status</th>
                        <th className="enterprise-table-head-cell">Versão</th>
                        <th className="enterprise-table-head-cell">Atualizado</th>
                        <th className="enterprise-table-head-cell" />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((t) => (
                        <tr key={t.id} className="enterprise-table-row">
                          <td className="enterprise-table-cell font-medium">{t.title}</td>
                          <td className="enterprise-table-cell enterprise-text-secondary">{t.category || '—'}</td>
                          <td className="enterprise-table-cell enterprise-text-secondary text-[11px]">
                            {TEMPLATE_TYPE_LABELS[t.type] ?? t.type}
                          </td>
                          <td className="enterprise-table-cell">
                            <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${statusBadge[t.status]}`}>
                              {statusLabel[t.status]}
                            </span>
                          </td>
                          <td className="enterprise-table-cell font-mono text-[11px] enterprise-text-secondary">
                            {t.version || '—'}
                          </td>
                          <td className="enterprise-table-cell enterprise-text-secondary">
                            {t.updated_at ? new Date(t.updated_at).toLocaleDateString('pt-BR') : '—'}
                          </td>
                          <td className="enterprise-table-cell text-right">
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-enterprise-panel enterprise-text-secondary transition-colors"
                              title="Duplicar template"
                            >
                              <Copy size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ModuleCard>
          )}
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
